import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Badge,
  Button,
  Loader,
  Input,
  Select,
  Title,
  Body,
  Subline,
  Icon,
  Table,
} from '../components/kern';
import type { BadgeVariant, IconName } from '../components/kern';

interface AuditLog {
  id: number;
  level: 'debug' | 'info' | 'warn' | 'error';
  category: string;
  action: string;
  message: string;
  details: Record<string, unknown> | null;
  project_id: number | null;
  feedback_id: number | null;
  user_id: number | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
  project_name: string | null;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface LogStats {
  byLevel: Record<string, number>;
  today: number;
}

const levelConfig: Record<string, { icon: IconName; variant: BadgeVariant; label: string }> = {
  debug: { icon: 'info', variant: 'info', label: 'Debug' },
  info: { icon: 'success', variant: 'success', label: 'Information' },
  warn: { icon: 'warning', variant: 'warning', label: 'Warnung' },
  error: { icon: 'warning', variant: 'warning', label: 'Fehler' },
};

export function AuditLogPage() {
  const { apiCall } = useAuth();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0,
  });
  const [stats, setStats] = useState<LogStats | null>(null);
  const [categories, setCategories] = useState<{ category: string; count: number }[]>([]);
  const [expandedLog, setExpandedLog] = useState<number | null>(null);

  // Filter
  const [filterLevel, setFilterLevel] = useState<string>('');
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [filterSearch, setFilterSearch] = useState<string>('');

  const loadLogs = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('limit', '50');
        if (filterLevel) params.set('level', filterLevel);
        if (filterCategory) params.set('category', filterCategory);
        if (filterSearch) params.set('search', filterSearch);

        const response = await apiCall(`/api/v1/audit-logs?${params.toString()}`);
        const data = await response.json();

        if (data.success) {
          setLogs(data.logs);
          setPagination(data.pagination);
        }
      } catch (error) {
        console.error('Fehler beim Laden der Logs:', error);
      } finally {
        setLoading(false);
      }
    },
    [apiCall, filterLevel, filterCategory, filterSearch]
  );

  const loadStats = useCallback(async () => {
    try {
      const response = await apiCall('/api/v1/audit-logs/stats');
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Statistiken:', error);
    }
  }, [apiCall]);

  const loadCategories = useCallback(async () => {
    try {
      const response = await apiCall('/api/v1/audit-logs/categories');
      const data = await response.json();
      if (data.success) {
        setCategories(data.categories);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Kategorien:', error);
    }
  }, [apiCall]);

  useEffect(() => {
    loadLogs();
    loadStats();
    loadCategories();
  }, [loadLogs, loadStats, loadCategories]);

  const handleCleanup = async () => {
    if (!confirm('Alle Logs älter als 30 Tage löschen?')) return;

    try {
      const response = await apiCall('/api/v1/audit-logs/cleanup?days=30', {
        method: 'DELETE',
      });
      const data = await response.json();
      if (data.success) {
        alert(data.message);
        loadLogs();
        loadStats();
      }
    } catch (error) {
      console.error('Fehler beim Aufräumen:', error);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <div className="audit-log-page">
      <header className="page-header">
        <div>
          <Title>
            <Icon name="clipboard-text" size="medium" /> Audit-Logs
          </Title>
          <Subline>System- und Aktivitätsprotokolle</Subline>
        </div>
        <Button variant="secondary" onClick={handleCleanup}>
          <Icon name="delete" size="small" /> Alte Logs löschen
        </Button>
      </header>

      {/* Statistiken */}
      {stats && (
        <div className="kern-container">
          <div className="kern-row">
            <div className="kern-col-xl-3 kern-col-md-6 kern-col-sm-12">
              <article className="kern-card stat-card">
                <div className="kern-card__container">
                  <div className="stat-card-content">
                    <div className="stat-icon stat-icon--primary">
                      <Icon name="chart-bar" size="large" />
                    </div>
                    <div className="stat-content">
                      <span className="kern-heading-large">{stats.today}</span>
                      <Body size="small" muted>
                        Heute
                      </Body>
                    </div>
                  </div>
                </div>
              </article>
            </div>

            <div className="kern-col-xl-3 kern-col-md-6 kern-col-sm-12">
              <article className="kern-card stat-card">
                <div className="kern-card__container">
                  <div className="stat-card-content">
                    <div className="stat-icon stat-icon--info">
                      <Icon name="information" size="large" />
                    </div>
                    <div className="stat-content">
                      <span className="kern-heading-large">{stats.byLevel.info || 0}</span>
                      <Body size="small" muted>
                        Info
                      </Body>
                    </div>
                  </div>
                </div>
              </article>
            </div>

            <div className="kern-col-xl-3 kern-col-md-6 kern-col-sm-12">
              <article className="kern-card stat-card">
                <div className="kern-card__container">
                  <div className="stat-card-content">
                    <div className="stat-icon stat-icon--warning">
                      <Icon name="alert" size="large" />
                    </div>
                    <div className="stat-content">
                      <span className="kern-heading-large">{stats.byLevel.warn || 0}</span>
                      <Body size="small" muted>
                        Warnungen
                      </Body>
                    </div>
                  </div>
                </div>
              </article>
            </div>

            <div className="kern-col-xl-3 kern-col-md-6 kern-col-sm-12">
              <article className="kern-card stat-card">
                <div className="kern-card__container">
                  <div className="stat-card-content">
                    <div className="stat-icon stat-icon--danger">
                      <Icon name="close-circle" size="large" />
                    </div>
                    <div className="stat-content">
                      <span className="kern-heading-large">{stats.byLevel.error || 0}</span>
                      <Body size="small" muted>
                        Fehler
                      </Body>
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="filter-bar">
        <Select
          id="filterLevel"
          value={filterLevel}
          onChange={(e) => setFilterLevel(e.target.value)}
          aria-label="Log-Level Filter"
          options={[
            { value: '', label: 'Alle Level' },
            { value: 'debug', label: 'Debug' },
            { value: 'info', label: 'Info' },
            { value: 'warn', label: 'Warnung' },
            { value: 'error', label: 'Fehler' },
          ]}
        />

        <Select
          id="filterCategory"
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          aria-label="Kategorie Filter"
          options={[
            { value: '', label: 'Alle Kategorien' },
            ...categories.map((cat) => ({
              value: cat.category,
              label: `${cat.category} (${cat.count})`,
            })),
          ]}
        />

        <Input
          id="filterSearch"
          className="filter-search"
          placeholder="Suche..."
          value={filterSearch}
          onChange={(e) => setFilterSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && loadLogs(1)}
          aria-label="Suchfeld"
        />

        <Button variant="primary" onClick={() => loadLogs(1)}>
          <Icon name="magnify" size="small" /> Suchen
        </Button>
      </div>

      {/* Log-Liste */}
      {loading ? (
        <div className="loading-container">
          <Loader />
          <Body>Lade Logs...</Body>
        </div>
      ) : logs.length === 0 ? (
        <div className="empty-state">
          <Icon name="clipboard-text" size="large" className="empty-state-icon" />
          <Body>Keine Logs gefunden</Body>
        </div>
      ) : (
        <div className="kern-container">
          <div className="kern-row">
            <div className="kern-col-12">
              <Table<AuditLog>
                data={logs}
                keyExtractor={(log) => log.id}
                striped
                expandedRowPredicate={(log) => expandedLog === log.id && !!log.details}
                expandedRowContent={(log) => (
                  <pre className="details-pre details-pre--full">
                    {JSON.stringify(log.details ?? {}, null, 2)}
                  </pre>
                )}
                columns={[
                  {
                    key: 'level',
                    header: 'Level',
                    render: (log) => (
                      <Badge variant={levelConfig[log.level]?.variant || 'neutral'}>
                        <Icon name={levelConfig[log.level]?.icon || 'information'} size="small" />
                        {levelConfig[log.level]?.label || log.level.toUpperCase()}
                      </Badge>
                    ),
                  },
                  {
                    key: 'category',
                    header: 'Kategorie',
                    render: (log) => <Badge variant="neutral">{log.category || '-'}</Badge>,
                  },
                  { key: 'action', header: 'Aktion' },
                  { key: 'message', header: 'Nachricht' },
                  {
                    key: 'project',
                    header: 'Projekt',
                    render: (log) => log.project_name || log.project_id || '-',
                  },
                  {
                    key: 'feedback_id',
                    header: 'Feedback',
                    render: (log) => log.feedback_id ?? '-',
                  },
                  {
                    key: 'user_id',
                    header: 'User',
                    render: (log) => log.user_id ?? '-',
                  },
                  {
                    key: 'ip_address',
                    header: 'IP',
                    render: (log) => log.ip_address || '-',
                  },
                  {
                    key: 'user_agent',
                    header: 'User-Agent',
                    render: (log) => log.user_agent || '-',
                  },
                  {
                    key: 'created_at',
                    header: 'Zeit',
                    render: (log) => formatDate(log.created_at),
                  },
                  {
                    key: 'details',
                    header: 'Details',
                    render: (log) =>
                      log.details ? (
                        <Button
                          variant="secondary"
                          icon={expandedLog === log.id ? 'close' : 'more-vert'}
                          onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                          aria-expanded={expandedLog === log.id}
                        >
                          {expandedLog === log.id ? 'Schließen' : 'Anzeigen'}
                        </Button>
                      ) : (
                        '-'
                      ),
                  },
                ]}
              />
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="pagination">
          <Button
            variant="secondary"
            disabled={pagination.page <= 1}
            onClick={() => loadLogs(pagination.page - 1)}
          >
            <Icon name="chevron-left" size="small" /> Zurück
          </Button>
          <Body className="pagination-info">
            Seite {pagination.page} von {pagination.pages} ({pagination.total} Einträge)
          </Body>
          <Button
            variant="secondary"
            disabled={pagination.page >= pagination.pages}
            onClick={() => loadLogs(pagination.page + 1)}
          >
            Weiter <Icon name="chevron-right" size="small" />
          </Button>
        </div>
      )}

      <style>{`
        .audit-log-page {
          padding: 24px;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 24px;
        }

        .stats-row {
          display: flex;
          gap: 16px;
          margin-bottom: 24px;
        }

        .stat-card {
          display: flex;
          align-items: center;
          gap: 12px;
          background: var(--kern-bg-secondary);
          padding: 16px 20px;
          border-radius: 8px;
          flex: 1;
        }

        .stat-card.error {
          background: #fee2e2;
        }

        .stat-icon {
          font-size: 24px;
        }

        .stat-value {
          font-size: 24px;
          font-weight: 700;
        }

        .stat-label {
          font-size: 12px;
          color: var(--kern-text-secondary);
        }

        .filter-bar {
          display: flex;
          gap: 12px;
          margin-bottom: 16px;
          padding: 16px;
          background: var(--kern-bg-secondary);
          border-radius: 8px;
        }

        .filter-bar .kern-input-select,
        .filter-bar .kern-input-text {
          min-width: 150px;
        }

        .filter-bar .filter-search {
          flex: 1;
        }

        .audit-log-page .kern-table__head .kern-table__header:first-child,
        .audit-log-page .kern-table__body .kern-table__row > :first-child {
          width: 18%;
          min-width: 18%;
        }

        .details-cell {
          display: flex;
          flex-direction: column;
          gap: 8px;
          align-items: flex-start;
        }

        .details-pre {
          margin: 0;
          background: #1e1e1e;
          color: #d4d4d4;
          padding: 8px;
          border-radius: 4px;
          width: 100%;
          max-width: none;
          overflow-x: auto;
          font-size: 18px;
          font-family: 'Consolas', 'Monaco', monospace;
        }

        .details-pre--full {
          display: block;
        }

        .loading,
        .empty-state {
          text-align: center;
          padding: 48px;
          color: var(--kern-text-secondary);
        }

        .pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 16px;
          margin-top: 24px;
        }

        .pagination-info {
          color: var(--kern-text-secondary);
          font-size: 14px;
        }
      `}</style>
    </div>
  );
}
